import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useProject } from "../ProjectContext";
import { upload_file_api, get_uploaded_file_api, automatic_process_api, subscribe_task_stream } from "../api/project.api";

const ProjectLanding = () => {
    const navigate = useNavigate();
    const { user, setRedirectPath, userId } = useAuth();
    const { activeProject, selectProject } = useProject();
    const [uploadStatus, setUploadStatus] = React.useState("idle"); // idle, uploading, uploaded
    const [uploadedFileName, setUploadedFileName] = React.useState("");
    const [infoMessage, setInfoMessage] = React.useState("");
    const [isCheckingExisting, setIsCheckingExisting] = React.useState(true);
    const [showReuploadModal, setShowReuploadModal] = React.useState(false); // ← Add modal state
    const [pendingFile, setPendingFile] = React.useState(null); // ← Store pending file for reupload
    const [isProcessingAutomatic, setIsProcessingAutomatic] = React.useState(false); // ← Auto processing state
    const [processingStageMessage, setProcessingStageMessage] = React.useState(""); // ← Current stage message
    const unsubscribeRef = useRef(null); // ← Store unsubscribe function

    useEffect(() => {
        if (!user) {
            setRedirectPath("/project");
            navigate("/login");
            return;
        }

        if (!activeProject) {
            navigate("/project");
            return;
        }

        // -------- Check for Existing Upload on Mount --------
        const checkExistingUpload = async () => {
            if (!userId || !activeProject?.ProjectID) {
                setIsCheckingExisting(false);
                return;
            }

            try {
                setIsCheckingExisting(true);
                const response = await get_uploaded_file_api(userId, activeProject.ProjectID);
                
                // Check if file upload exists - be more flexible with response structure
                if (response) {
                    // Try different possible response structures
                    let hasFile = false;
                    let fileName = "";
                    
                    // Check various possible structures
                    if (response.status && response.fileName) {
                        hasFile = true;
                        fileName = response.fileName;
                    } else if (response.uploaded && response.filename) {
                        hasFile = true;
                        fileName = response.filename;
                    } else if (response.success && response.file) {
                        hasFile = true;
                        fileName = response.file;
                    } else if (response.data && response.data.fileName) {
                        hasFile = true;
                        fileName = response.data.fileName;
                    }
                    
                    if (hasFile && fileName) {
                        // Restore upload state
                        setUploadStatus("uploaded");
                        setUploadedFileName(fileName);
                        setInfoMessage("File uploaded successfully.");
                    } else {
                        // No upload found - fresh start
                        setUploadStatus("idle");
                        setUploadedFileName("");
                        setInfoMessage("");
                    }
                } else {
                    setUploadStatus("idle");
                    setUploadedFileName("");
                    setInfoMessage("");
                }
            } catch (error) {
                // Don't show error to user - just start fresh
                setUploadStatus("idle");
                setUploadedFileName("");
                setInfoMessage("");
            } finally {
                setIsCheckingExisting(false);
            }
        };

        checkExistingUpload();
        // eslint-disable-next-line
    }, [user, activeProject, userId]);

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (file) {
            // Check if file is already uploaded - show confirmation modal
            if (uploadStatus === "uploaded") {
                setPendingFile(file);
                setShowReuploadModal(true);
                return;
            }

            // Proceed with normal upload
            await performFileUpload(file);
        }
    };

    const performFileUpload = async (file) => {
        setUploadStatus("uploading");
        setInfoMessage(""); // reset process status for new upload
        try {
            const response = await upload_file_api(file, userId, activeProject.ProjectID);
            if (response) {
                setUploadStatus("uploaded");
                setUploadedFileName(file.name);
                setInfoMessage("File uploaded successfully.");
                alert("File uploaded successfully.");
                activeProject.currentStage = "ready";
                selectProject(activeProject);
            } else {
                setUploadStatus("idle");
                setInfoMessage("Upload failed.");
                alert("Upload failed.");
            }
        } catch (error) {
            setUploadStatus("idle");
            setInfoMessage("Upload failed.");
            alert("Upload failed.");
        }
    };

    const handleConfirmReupload = async () => {
        setShowReuploadModal(false);
        if (pendingFile) {
            await performFileUpload(pendingFile);
            setPendingFile(null);
        }
    };

    const handleCancelReupload = () => {
        setShowReuploadModal(false);
        setPendingFile(null);
        // Navigate to project dashboard
        navigate("/project");
    };

    const handleAutomaticProcessing = async () => {
        if (uploadStatus !== "uploaded") {
            alert("Please upload a CSV file before proceeding to automatic processing.");
            return;
        }

        setIsProcessingAutomatic(true);
        setProcessingStageMessage("Starting automatic processing...");
        try {
            const response = await automatic_process_api(userId, activeProject.ProjectID);
            if (response && response.task_id) {
                // Subscribe to SSE stream and update stage message
                const unsubscribe = subscribe_task_stream(response.task_id, (evt) => {
                    if (evt.state === 'PROCESSING') {
                        // Update stage message if available
                        if (evt.stageMessage) {
                            setProcessingStageMessage(evt.stageMessage);
                        } else if (evt.status) {
                            setProcessingStageMessage(evt.status);
                        }
                    }
                    
                    if (evt.state === 'SUCCESS') {
                        // Navigate to cluster page when everything is done
                        setProcessingStageMessage("Processing complete!");
                        activeProject.currentStage = "clustering";
                        selectProject(activeProject);
                        setTimeout(() => {
                            navigate("/project/cluster");
                            setIsProcessingAutomatic(false);
                            setProcessingStageMessage("");
                            if (unsubscribe) unsubscribe();
                        }, 1000); // Small delay to show completion message
                    }
                    
                    if (evt.state === 'FAILURE') {
                        alert("Automatic processing failed: " + (evt.error || "Unknown error"));
                        setIsProcessingAutomatic(false);
                        setProcessingStageMessage("");
                        if (unsubscribe) unsubscribe();
                    }
                });
                unsubscribeRef.current = unsubscribe;
            } else {
                alert("Automatic processing failed. Please try again.");
                setIsProcessingAutomatic(false);
                setProcessingStageMessage("");
            }
        } catch (error) {
            alert("Automatic processing failed. Please try again.");
            setIsProcessingAutomatic(false);
            setProcessingStageMessage("");
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (unsubscribeRef.current) {
                unsubscribeRef.current();
                unsubscribeRef.current = null;
            }
        };
    }, []);

    const handleManualProcessing = () => {
        if (uploadStatus !== "uploaded") {
            alert("Please upload a CSV file before proceeding to manual processing.");
            return;
        }
        navigate("/project/embedding");
    };

    if (!activeProject) return null;

    // ========== Loading State ==========
    if (isCheckingExisting) {
        return (
            <div className="w-full h-full flex flex-col pt-1">
                <div className="px-6 py-4 bg-white border-b border-gray-200">
                    <h1 className="text-xl font-semibold text-[#1a2340]">
                        {activeProject.projectName}
                    </h1>
                </div>
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#ff2e2e] mx-auto mb-4"></div>
                        <p className="text-gray-600 text-lg">Checking for existing files...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full h-full flex flex-col">
            {/* Small Header with Project Name */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 bg-white border-b border-gray-200">
                <h1 className="text-lg sm:text-xl font-semibold text-[#1a2340] break-words">
                    {activeProject.projectName}
                </h1>
            </div> 

            {/* Content Area */}
            <div className="w-full max-w-4xl mx-auto flex-1 py-4 sm:py-6 md:py-8">
                {/* Upload Block */}
                <div className="flex flex-col items-center justify-center min-h-full">
                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_6px_18px_rgba(20,20,40,0.09)] p-4 sm:p-6 md:p-8 mb-4 sm:mb-6 w-full">
                        <h2 className="text-xl sm:text-2xl font-bold text-[#1a2340] mb-2 sm:mb-3">
                            Upload Ideas
                        </h2>
                        <p className="text-sm sm:text-base text-[#666] mb-4 sm:mb-6">
                            Upload the generated ideas as CSV file to get started with processing
                        </p>

                        <label className="inline-block">
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileUpload}
                                onClick={e => { e.currentTarget.value = null; }}
                                className="hidden"
                                id="csv-upload"
                                disabled={uploadStatus === "uploading" || isProcessingAutomatic}
                            />
                            <span className={`w-full sm:w-auto px-6 sm:px-8 py-2.5 sm:py-3 bg-[#ff2e2e] text-white font-bold text-sm sm:text-base rounded-xl shadow-[0_6px_22px_rgba(255,46,46,0.16)] transition-all duration-200 inline-flex items-center justify-center gap-2 ${uploadStatus === "uploading" || isProcessingAutomatic
                                    ? "opacity-70 cursor-not-allowed"
                                    : "hover:bg-[#e60000] transform hover:scale-105 cursor-pointer"
                                }`}>
                                {uploadStatus === "uploading" ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Uploading...
                                    </>
                                ) : uploadStatus === "uploaded" ? (
                                    "Re-upload CSV File"
                                ) : (
                                    "Upload CSV File"
                                )}
                            </span>
                        </label>

                        {/* Status Message */}
                        {infoMessage && (
                            <p className={`mt-4 text-sm ${uploadStatus === "uploaded" ? "text-green-600" : "text-red-600"}`}>
                                {infoMessage}
                            </p>
                        )}

                        {uploadedFileName && uploadStatus === "uploaded" && (
                            <p className="mt-2 text-sm text-gray-600">
                                File: {uploadedFileName}
                            </p>
                        )}
                    </div>

                    {/* Processing Mode Block */}
                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-[0_6px_18px_rgba(20,20,40,0.09)] p-4 sm:p-6 md:p-8 w-full">
                        <h2 className="text-xl sm:text-2xl font-bold text-[#1a2340] mb-2 sm:mb-3">
                            Processing Mode
                        </h2>
                        <p className="text-sm sm:text-base text-[#666] mb-4 sm:mb-6">
                            Choose how you want to process the uploaded ideas
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={handleAutomaticProcessing}
                                disabled={uploadStatus !== "uploaded" || isProcessingAutomatic}
                                className={`w-full sm:flex-1 px-4 sm:px-6 py-3 sm:py-4 font-bold text-base sm:text-lg rounded-xl border-2 transition-all duration-200 transform inline-flex items-center justify-center gap-2 ${
                                    uploadStatus === "uploaded" && !isProcessingAutomatic
                                        ? "bg-white text-[#1a2340] border-[#1a2340] hover:bg-[#1a2340] hover:text-white hover:scale-105"
                                        : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                                }`}
                            >
                                {isProcessingAutomatic ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        Automatic Processing
                                    </>
                                )}
                            </button>

                            <button
                                onClick={handleManualProcessing}
                                disabled={uploadStatus !== "uploaded" || isProcessingAutomatic}
                                className={`w-full sm:flex-1 px-4 sm:px-6 py-3 sm:py-4 font-bold text-base sm:text-lg rounded-xl border-2 transition-all duration-200 transform ${
                                    uploadStatus === "uploaded" && !isProcessingAutomatic
                                        ? "bg-white text-[#1a2340] border-[#1a2340] hover:bg-[#1a2340] hover:text-white hover:scale-105"
                                        : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                                }`}
                            >
                                Manual Processing
                            </button>
                        </div>

                        {uploadStatus !== "uploaded" && (
                            <p className="mt-4 text-sm text-gray-500 text-center">
                                Upload a CSV file to enable processing options
                            </p>
                        )}

                        {isProcessingAutomatic && (
                            <p className="mt-4 text-sm text-blue-600 text-center">
                                Running automatic processing pipeline...
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* ========== Processing Loader Overlay ========== */}
            {isProcessingAutomatic && (
                <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl p-6 sm:p-8 md:p-12 max-w-md w-full mx-4 transform scale-100 transition-all text-center">
                        {/* Loading Spinner */}
                        <div className="flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6">
                            <div className="animate-spin rounded-full h-16 w-16 sm:h-20 sm:w-20 border-4 border-[#f53838] border-t-transparent"></div>
                        </div>
                        
                        {/* Stage Message */}
                        <h3 className="text-xl sm:text-2xl font-bold text-[#1a2340] mb-3 sm:mb-4">
                            Processing...
                        </h3>
                        <p className="text-base sm:text-lg text-gray-700 mb-2 break-words">
                            {processingStageMessage || "Starting automatic processing..."}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4">
                            Please wait while we process your data. This may take a few minutes.
                        </p>
                    </div>
                </div>
            )}

            {/* ========== Reupload Confirmation Modal ========== */}
            {showReuploadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 transform scale-100 transition-all">
                        {/* Warning Icon */}
                        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-yellow-100 rounded-full">
                            <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                        </div>

                        {/* Modal Content */}
                        <div className="text-center">
                            <h3 className="text-xl font-bold text-[#1a2340] mb-4">
                                Warning: Data Loss
                            </h3>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                Re-uploading files will <strong>erase all previously generated outputs</strong> including embeddings, reductions, and cluster analysis. 
                            </p>
                            <p className="text-gray-600 mb-8 leading-relaxed">
                                Consider creating a new project to preserve your current progress.
                            </p>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={handleCancelReupload}
                                    className="flex-1 px-6 py-3 bg-white text-[#1a2340] font-bold text-base rounded-xl border-2 border-[#1a2340] hover:bg-[#1a2340] hover:text-white transition-all duration-200"
                                >
                                    Create New Project
                                </button>
                                <button
                                    onClick={handleConfirmReupload}
                                    className="flex-1 px-6 py-3 bg-[#ff2e2e] text-white font-bold text-base rounded-xl shadow-[0_6px_22px_rgba(255,46,46,0.16)] hover:bg-[#e60000] transition-all duration-200"
                                >
                                    Re-upload Anyway
                                </button>
                            </div>

                            {/* Cancel Link */}
                            <button
                                onClick={() => setShowReuploadModal(false)}
                                className="mt-4 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProjectLanding;